import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import isEqual from 'lodash/isEqual';
import { ReactElement, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import useRole from '~/store/user/hooks/useRole';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { IGetActTeamMembersRequestPayload } from '~/store/actTeamMember/types';
import { extractErrorMessage } from '~/utils/error/error';
import getButtonText from '~/utils/text/getButtonText';
import useMemoCompare from '~/hooks/useMemoCompare';
import { IActTeamMember, Status as UserStatus } from '~/services/api/actTeamMember/types';
import { IParams as IValsParams } from '~/services/api/types';

import { EDIT_TEAM_MEMBER, VIEW_ACT_TEAM_MEMBER } from '~/ui/constants/paths';
import { multipleStatusesOptions, sortingOptions } from '~/ui/constants/sortingOptions';
import { red } from '~/ui/constants/colors';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import Input from '~/ui/components/inputs/Input';
import AutoSubmit from '~/ui/components/inputs/AutoSubmit';
import Status from '~/ui/components/common/Status';
import getAssignedTeamsCount from '~/ui/pages/Clinics/ViewClinic/utils/getAssignedTeamsCount';
import editDisabledIcon from '~/ui/assets/images/editDisabled.svg';
import editIcon from '~/ui/assets/images/editGrey.svg';
import teamLeaderIcon from '~/ui/assets/images/teamLeader.svg';
import { ReactComponent as GlobeIcon } from '~/ui/assets/images/globe.svg';
import { IUserRole } from '~/types';

import variables from '~/ui/assets/styles/colors.module.scss';

import styles from './TeamMembers.module.scss';

interface IProps {
  clinicId: string;
  actTeamId: string;
  noActions?: boolean;
  setTeamMember: (v: IActTeamMember) => void;
}

let prevRequestPayload: IGetActTeamMembersRequestPayload;

const Table = ({ clinicId, actTeamId, noActions, setTeamMember }: IProps): ReactElement => {
  const navigate = useNavigate();

  const filters = useStoreState(state => state.actTeamMember.filters);
  const { id: userId } = useStoreState(state => state.user.current);
  const formMethods = useForm({ defaultValues: filters });

  const { isSuperAdmin, isProductOwner, isProgramAssistant, role } = useRole();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const watchValues = watch();

  const actTeamMembers = useStoreState(state => state.actTeamMember.list);
  const pagination = useStoreState(state => state.actTeamMember.pagination);

  const showError = useStoreActions(state => state.snackbar.showError);
  const onGetActTeamMembers = useStoreActions(actions => actions.actTeamMember.onGetActTeamMembers);

  const memoizedFilters = useMemoCompare(filters, (prev, next) => prev && isEqual(prev, next));

  const onMount = useCallback(async () => {
    try {
      const requestPayload = {
        clinicId,
        actTeamId,
        params: { pageSize: pagination.pageSize, pageNumber: 1, ...memoizedFilters },
      };

      if (isEqual(prevRequestPayload, requestPayload)) {
        return;
      }

      await onGetActTeamMembers(requestPayload);
      prevRequestPayload = requestPayload;
    } catch (e) {
      showError(extractErrorMessage(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actTeamId, clinicId, onGetActTeamMembers, showError]);

  const onSubmit = async (vals: IValsParams) => {
    const { pageSize } = pagination;
    const payload = { clinicId, actTeamId, params: { pageSize, pageNumber: 1, ...vals } };
    await onGetActTeamMembers(payload);
  };

  useEffect(() => {
    onMount();

    return () => {
      prevRequestPayload = undefined;
    };
  }, [onMount]);

  const handlePagination = (pageNumber: number, pageSize: number) => {
    onGetActTeamMembers({
      clinicId,
      actTeamId,
      params: { pageNumber, pageSize, ...watchValues },
    });
  };

  return (
    <TableContainer>
      <Box sx={{ p: 2 }}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
              <Grid
                size={{
                  sm: 4,
                }}
              >
                <Input
                  startAdornment={<SearchIcon color="primary" />}
                  placeholder="Search members"
                  register={register}
                  name="name"
                  errors={errors}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <SelectComponent
                  label="Sort By"
                  control={control}
                  name="sorting"
                  errors={errors}
                  options={sortingOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <Grid
                size={{
                  sm: 2,
                }}
              >
                <SelectComponent
                  label="Filter By Status"
                  control={control}
                  name="status"
                  errors={errors}
                  options={multipleStatusesOptions}
                  color={red}
                  hideSelectedOptions={false}
                />
              </Grid>
              <AutoSubmit debounce={1000} initialValues={filters} onSubmit={onSubmit} />
            </Grid>
          </form>
        </FormProvider>
      </Box>
      <MUITable>
        <TableHead>
          <TableRow>
            <TableCell width={1} />
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
            {!noActions && (
              <>
                <TableCell>Actions</TableCell>
                <TableCell />
                <TableCell />
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {actTeamMembers.map(actTeamMember => {
            const currentClinicRoles = actTeamMember?.roles?.filter(
              clinicRole => clinicRole.clinic.id === Number(clinicId),
            );
            const currentRoleTeams = role?.teams?.map(item => item.id) ?? [];
            // check if user team leader in current team
            const isTeamLeader = currentClinicRoles
              ?.find(teamLeaderRole => teamLeaderRole.name === IUserRole.ActTeamLeader)
              ?.teams?.some(item => item.id === Number(actTeamId));

            // check if user team member in current team
            const isTeamMember = currentClinicRoles
              ?.find(memberRole => memberRole.name === IUserRole.ActTeamMember)
              ?.teams?.some(item => item.id === Number(actTeamId));

            // check if user is global user
            const isGlobalUser = currentClinicRoles?.find(
              globalUserRole => globalUserRole.name === IUserRole.GlobalUser,
            );
            const hasOutOfScopeTeamsForPa =
              isProgramAssistant &&
              Boolean(
                actTeamMember?.teams?.some(memberTeam => !currentRoleTeams.includes(memberTeam.id)),
              );

            const isOnlyGlobalUser = isGlobalUser && !isTeamLeader && !isTeamMember;

            const isCurrentUser = actTeamMember.id === userId;

            return (
              <TableRow
                key={actTeamMember.id}
                style={{
                  borderLeft: isTeamLeader ? `3px solid ${variables.colorRed}` : '',
                }}
              >
                <TableCell width={1}>
                  {isTeamLeader && (
                    <span>
                      <img src={teamLeaderIcon} alt="teamLeader" />
                    </span>
                  )}
                  {isGlobalUser && <GlobeIcon className={styles.globeIcon} />}
                </TableCell>
                <TableCell>
                  <Link
                    className={classNames(styles.link, {
                      [styles.disabled]: isSuperAdmin || isProductOwner,
                    })}
                    to={{
                      pathname: VIEW_ACT_TEAM_MEMBER.replace(':actTeamId', actTeamId).replace(
                        ':teamMemberId',
                        String(actTeamMember.id),
                      ),
                    }}
                  >
                    {actTeamMember.firstName}
                  </Link>
                </TableCell>

                <TableCell>{actTeamMember.lastName}</TableCell>
                <TableCell>{actTeamMember.email}</TableCell>
                <TableCell>
                  <Status status={actTeamMember.status} />
                </TableCell>
                {!noActions && (
                  <TableCell>
                    {['Deactivated', 'Locked'].includes(actTeamMember.status) ? (
                      <IconButton disabled size="large">
                        <img src={editDisabledIcon} alt="disabled" />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() =>
                          navigate(
                            EDIT_TEAM_MEMBER.replace(':actTeamId', actTeamId).replace(
                              ':teamMemberId',
                              String(actTeamMember.id),
                            ),
                          )
                        }
                        size="large"
                      >
                        <img src={editIcon} alt="edit" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
                {!noActions && (
                  <TableCell className={styles.column}>
                    <button
                      type="button"
                      className={styles.actionButton}
                      onClick={() => setTeamMember(actTeamMember)}
                      disabled={
                        (actTeamMember.status === 'Active' && isCurrentUser) ||
                        (actTeamMember.status === 'Active' && hasOutOfScopeTeamsForPa)
                      }
                    >
                      {getButtonText(actTeamMember.status)}
                    </button>
                    {actTeamMember.status === 'Pending' && (
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() =>
                          setTeamMember({
                            ...actTeamMember,
                            status: 'VerifyingEmail' as UserStatus,
                          })
                        }
                      >
                        Archive
                      </button>
                    )}
                  </TableCell>
                )}
                {!noActions && (
                  <TableCell>
                    <button
                      type="button"
                      disabled={
                        actTeamMember.status !== 'Active' ||
                        (isTeamLeader && !isTeamMember) ||
                        isOnlyGlobalUser || // disable if user has only team leader role in current team or inactive
                        getAssignedTeamsCount(actTeamMember) <= 1
                      }
                      className={styles.actionButton}
                      onClick={() =>
                        setTeamMember({ ...actTeamMember, status: 'Assigned' as UserStatus })
                      }
                    >
                      Unassign
                    </button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </MUITable>
      <TablePagination
        component="div"
        count={pagination.total}
        onPageChange={(_, pageNumber) => {
          handlePagination(pageNumber + 1, pagination.pageSize);
        }}
        onRowsPerPageChange={e => {
          handlePagination(1, Number(e.target.value));
        }}
        page={pagination.pageNumber - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

export default Table;
